import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { HelpBox } from 'components/v2/help-boxes';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { FieldPanelInput } from 'styles/v2/Styles.styled';
import DialogClearStyles, {
  DialogClearStylesButtonsFooter,
  DialogContent,
  DialogFooter,
} from 'components/accounts/DialogClearStyles';
import { toaster } from 'components/v2/toast';
import { HeaderDialog } from 'styles/Styles.styled';
import { ApiException } from 'services/EpdClient';

type TProps = {
  visible: boolean;
  onHide(): void;
};

const ImpersonateDialog: React.FC<TProps> = ({ onHide, visible }) => {
  return (
    <DialogClearStyles
      visible={visible}
      onHide={onHide}
      header={<HeaderDialog>Impersonate</HeaderDialog>}
      closable={false}
      draggable={false}
    >
      <DialogContent>
        <ImpersonateDialogContent onHide={onHide} />
      </DialogContent>
    </DialogClearStyles>
  );
};
export default ImpersonateDialog;

const ImpersonateDialogContent = ({ onHide }: { onHide(): void }) => {
  const [impersonateAs, setImpersonateAs] = React.useState<string>('');
  const { companyId, changeCompany, setDefaultCompany } = useContext(CompanyContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await AuthService.loginAs({ userName: impersonateAs, password: '' });
      const isMemberInOpenedCompany = result.memberships.some((x) => x.companyId == companyId && x.companyEnabled);
      if (!isMemberInOpenedCompany) {
        const nextCompanyId = result.latestLoginCompanyId ?? result.memberships[0]?.companyId;
        if (nextCompanyId) {
          await changeCompany(nextCompanyId);
        } else {
          setDefaultCompany();
        }
      }

      history.push(EpdLinks.dashboard());
      onHide();

      toaster({
        severity: 'yellow-exclamation-warning',
        summary: 'Impersonate mode has been enabled',
        details: (
          <span>
            You are currently logged in as <a href={`mailto:${result.email}`}>{result.email}</a>.
            <br />
            <br />
            Please log in again with your credentials to access the EPD Portal as a Super Admin.
          </span>
        ),
      });
    } catch (e) {
      if (e instanceof Error) {
        let message = e.message;
        if (e instanceof ApiException) {
          const response = JSON.parse(e.response);
          if (response.detail) {
            message = response.detail;
          }
        }
        toaster({
          severity: 'error',
          summary: 'Failed to impersonate',
          details: message,
        });
        console.error('Failed to impersonate', e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <HelpBox>
        <span>
          Please note, once you enable the impersonate function, you will enter the EPD Portal on behalf of the selected
          user.
        </span>
        <span>To access the EPD Portal with your own account, please log out and log in again using your credentials.</span>
      </HelpBox>
      <FieldWrapper>
        <FieldPanelInput>
          <WizardTextInputComponent
            autoFocus
            label="Add user email address"
            placeholder="Type email"
            value={impersonateAs}
            name="impersonateEmail"
            onChange={(e: any) => {
              setImpersonateAs(e.target.value);
            }}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                ev.preventDefault();
                ev.stopPropagation();
                handleSubmit();
              }
            }}
          />
        </FieldPanelInput>
      </FieldWrapper>

      <DialogFooter>
        <DialogClearStylesButtonsFooter>
          <SecondaryButton label="Cancel" onClick={() => onHide()} />
          <PrimaryButton
            label="Impersonate"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={impersonateAs.length === 0}
          />
        </DialogClearStylesButtonsFooter>
      </DialogFooter>
    </>
  );
};

const FieldWrapper = styled.div`
  margin-top: 30px;
  margin-bottom: 40px;
`;
